function normalizeFieldOptions (options) {
  if (!options?.length) return undefined
  return options.map(option => {
    if (typeof option === 'string') {
      return { value: option, valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE }
    }
    return {
      value: option.value ?? option.option,
      valueType: option.valueType ?? option.optionType ?? option.value_type
        ?? StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE
    }
  })
}

class StoreFormField {
  constructor (
    name,
    valueType,
    inputType,
    label,
    isRequired,
    position,
    options
  ) {
    if (!Object.values(StoreFormField.SUPPORTED_VALUE_TYPES).includes(valueType)) {
      throw new Error(`[StoreFormField] Invalid 'valueType' received: ${valueType}`)
    }

    if (!Object.values(StoreFormField.SUPPORTED_INPUT_TYPES).includes(inputType)) {
      throw new Error(`[StoreFormField] Invalid 'inputType' received: ${inputType}`)
    }

    this.name = name
    this.valueType = valueType
    this.inputType = inputType
    this.label = label
    this.isRequired = isRequired
    this.position = position
    this.options = normalizeFieldOptions(options)
  }

  getLegacyFieldValues () {
    if (StoreFormField.isDesignerInputType(this.inputType)) {
      return StoreFormField.buildLegacyFieldValuesForDesignerType(this)
    }

    if (!this.options?.length) return undefined
    return this.options.map(({ value, valueType }) => ({
      value,
      valueType
    }))
  }

  toJSONSchema () {
    if (this.inputType === StoreFormField.SUPPORTED_INPUT_TYPES.SPECIAL_DATETIME) {
      return {
        name: this.name,
        propertySchema: { type: 'string', format: 'date-time' },
        uiSchema: {
          type: 'Control',
          label: this.label,
          scope: `#/properties/${this.name}`,
          options: { format: 'date-time', legacyFieldType: 'special' }
        },
        required: this.isRequired ? [this.name] : [],
        position: this.position
      }
    }

    const legacyFieldValues = this.getLegacyFieldValues()

    const uiOptions = {
      legacyFieldType: 'dynamic'
    }

    if (StoreFormField.isDesignerInputType(this.inputType)) {
      uiOptions.designerInputType = this.inputType
    }

    return {
      name: this.name,
      propertySchema: { type: 'string' },
      uiSchema: {
        type: 'Control',
        label: this.label,
        scope: `#/properties/${this.name}`,
        options: uiOptions
      },
      required: this.isRequired ? [this.name] : [],
      position: this.position,
      legacyFieldValues
    }
  }

  static FromJSONSchema (propertyName, position, propertySchema, uiSchema, legacyFieldValues = null) {
    const property = propertySchema.properties[propertyName]
    const fieldUISchema = uiSchema.elements.find(({ scope }) => scope === `#/properties/${propertyName}`)
    const uiOptions = fieldUISchema?.options || {}
    const designerInputType = uiOptions.designerInputType
    const legacyFieldType = uiOptions.legacyFieldType
    const format = uiOptions.format

    const isLegacySpecial =
      legacyFieldType === 'special' ||
      format === 'date-time' ||
      format === 'date' ||
      propertyName === StoreFormField.SPECIAL_FIELD_ESTIMATED_TERMINATION

    if (isLegacySpecial) {
      return new StoreFormField(
        propertyName,
        StoreFormField.SUPPORTED_VALUE_TYPES.STRING,
        StoreFormField.SUPPORTED_INPUT_TYPES.SPECIAL_DATETIME,
        fieldUISchema?.label || propertyName,
        propertySchema.required?.includes(propertyName) ?? false,
        position,
        undefined
      )
    }

    if (
      designerInputType &&
      StoreFormField.isDesignerInputType(designerInputType)
    ) {
      return StoreFormField.fromLegacyDynamicField(
        propertyName,
        position,
        property,
        fieldUISchema,
        propertySchema,
        legacyFieldValues,
        designerInputType
      )
    }

    if (
      legacyFieldType === 'dynamic' ||
      legacyFieldValues?.[propertyName]?.length ||
      format === StoreFormField.SUPPORTED_INPUT_TYPES.DYNAMIC
    ) {
      return StoreFormField.fromLegacyDynamicField(
        propertyName,
        position,
        property,
        fieldUISchema,
        propertySchema,
        legacyFieldValues,
        StoreFormField.SUPPORTED_INPUT_TYPES.DYNAMIC
      )
    }

    if (!designerInputType && !format && property?.type === 'string') {
      return StoreFormField.fromLegacyDynamicField(
        propertyName,
        position,
        property,
        fieldUISchema,
        propertySchema,
        legacyFieldValues,
        StoreFormField.SUPPORTED_INPUT_TYPES.DYNAMIC
      )
    }

    const legacyInputType = format || StoreFormField.SUPPORTED_INPUT_TYPES.TEXT
    const resolvedInputType = Object.values(StoreFormField.SUPPORTED_INPUT_TYPES).includes(legacyInputType)
      ? legacyInputType
      : StoreFormField.SUPPORTED_INPUT_TYPES.TEXT

    const legacyForField = legacyFieldValues?.[propertyName]
    let options
    if (legacyForField?.length) {
      options = legacyForField.map(entry => ({
        value: String(entry.value),
        valueType: entry.valueType ?? entry.value_type ?? StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE
      }))
    } else if (property.enum?.length) {
      options = property.enum.map(value => ({
        value: String(value),
        valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE
      }))
    }

    return new StoreFormField(
      propertyName,
      property.type,
      resolvedInputType,
      fieldUISchema?.label || propertyName,
      propertySchema.required?.includes(propertyName) ?? false,
      position,
      options
    )
  }

  static fromLegacyDynamicField (
    propertyName,
    position,
    property,
    fieldUISchema,
    propertySchema,
    legacyFieldValues,
    inputType
  ) {
    const legacyForField = legacyFieldValues?.[propertyName]
    let options
    if (legacyForField?.length) {
      options = legacyForField.map(entry => ({
        value: String(entry.value),
        valueType: entry.valueType ?? entry.value_type ?? StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE
      }))
    } else if (property.enum?.length) {
      options = property.enum.map(value => ({
        value: String(value),
        valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE
      }))
    }

    return new StoreFormField(
      propertyName,
      StoreFormField.SUPPORTED_VALUE_TYPES.STRING,
      inputType,
      fieldUISchema?.label || propertyName,
      propertySchema.required?.includes(propertyName) ?? false,
      position,
      options
    )
  }

  static isLegacyNativeInputType (inputType) {
    return inputType === StoreFormField.SUPPORTED_INPUT_TYPES.DYNAMIC ||
      inputType === StoreFormField.SUPPORTED_INPUT_TYPES.SPECIAL_DATETIME
  }

  static isDesignerInputType (inputType) {
    return StoreFormField.DESIGNER_INPUT_TYPES.has(inputType)
  }

  static buildLegacyFieldValuesForDesignerType (field) {
    if (field.inputType === StoreFormField.SUPPORTED_INPUT_TYPES.SWITCH) {
      return [
        { value: 'true', valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE },
        { value: 'false', valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.VALUE }
      ]
    }

    if (StoreFormField.fieldInputTypeHasOptions(field.inputType)) {
      return field.options?.map(({ value, valueType }) => ({ value, valueType }))
    }

    return [
      {
        value: field.label || field.name,
        valueType: StoreFormField.SUPPORTED_OPTION_VALUE_TYPES.PLACEHOLDER
      }
    ]
  }

  static fieldInputTypeHasOptions (inputType) {
    return [
      StoreFormField.SUPPORTED_INPUT_TYPES.CHECKBOX,
      StoreFormField.SUPPORTED_INPUT_TYPES.RADIO,
      StoreFormField.SUPPORTED_INPUT_TYPES.SELECT,
      StoreFormField.SUPPORTED_INPUT_TYPES.DYNAMIC
    ].includes(inputType)
  }
}

StoreFormField.SUPPORTED_INPUT_TYPES = {
  TEXT: 'text',
  NUMBER: 'number',
  SELECT: 'select',
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
  SWITCH: 'switch',
  /** Legacy `field_type: dynamic` — mobile pick list + optional custom text input */
  DYNAMIC: 'dynamic',
  /** Legacy `field_type: special` — mobile date/time picker (mission estimated end) */
  SPECIAL_DATETIME: 'special_datetime'
}

StoreFormField.DESIGNER_INPUT_TYPES = new Set([
  StoreFormField.SUPPORTED_INPUT_TYPES.TEXT,
  StoreFormField.SUPPORTED_INPUT_TYPES.NUMBER,
  StoreFormField.SUPPORTED_INPUT_TYPES.SELECT,
  StoreFormField.SUPPORTED_INPUT_TYPES.RADIO,
  StoreFormField.SUPPORTED_INPUT_TYPES.CHECKBOX,
  StoreFormField.SUPPORTED_INPUT_TYPES.SWITCH
])

/** Mobile apps only render the date picker for this exact field name */
StoreFormField.SPECIAL_FIELD_ESTIMATED_TERMINATION = 'formtransl_estimated_termination'

StoreFormField.SUPPORTED_VALUE_TYPES = {
  STRING: 'string',
  NUMBER: 'number',
  BOOLEAN: 'boolean'
}

StoreFormField.SUPPORTED_OPTION_VALUE_TYPES = {
  VALUE: 'value',
  PLACEHOLDER: 'placeholder'
}

export default StoreFormField
